import { transliterate } from "transliteration";
import axios from "axios";

const cache = new Map();

// Detect if text contains non-Latin/non-English characters
const detectSourceLang = (text) => {
    const hasDevanagari = /[\u0900-\u097F]/.test(text); // Hindi, Marathi
    const hasTamil = /[\u0B80-\u0BFF]/.test(text);
    const hasArabic = /[\u0600-\u06FF]/.test(text);

    if (hasDevanagari) return 'hi';
    if (hasTamil) return 'ta';
    if (hasArabic) return 'ar';
    return 'en'; // default assume English
};

// Single API call with auto source detection
const fetchTranslation = async (text, from, to) => {
    const res = await axios.get('https://api.mymemory.translated.net/get', {
        params: { q: text, langpair: `${from}|${to}` }
    });
    const result = res.data.responseData.translatedText;
    // MyMemory sometimes returns error strings
    if (!result || result.toLowerCase().includes('invalid') || result.toLowerCase().includes('quota')) {
        return null;
    }
    return result;
};

export async function translateText(text, targetLang) {
    if (!text) return text;
    if (targetLang === 'en') {
        // If source is not English, translate TO English
        const sourceLang = detectSourceLang(text);
        if (sourceLang === 'en') return text;

        const cacheKey = `en::${text}`;
        if (cache.has(cacheKey)) return cache.get(cacheKey);

        try {
            const result = await fetchTranslation(text, sourceLang, 'en');
            const final = result || text;
            cache.set(cacheKey, final);
            return final;
        } catch {
            return text;
        }
    }

    const cacheKey = `${targetLang}::${text}`;
    if (cache.has(cacheKey)) return cache.get(cacheKey);

    let result = text;

    try {
        if (targetLang === 'enhi') {
            // Transliterate: convert any script → latin hinglish
            const emojiRegex = /([\u231A-\uD83E\uDDFF\uD83C-\uDBFF\uDC00-\uDFFF]+)/g;
            const emojis = text.match(emojiRegex) || [];
            const clean = text.replace(emojiRegex, '');
            result = transliterate(clean).trim() + (emojis.length ? ' ' + emojis.join(' ') : '');
        } else {
            const langMap = { hi: 'hi', ta: 'ta', mr: 'mr' };
            const to = langMap[targetLang];
            if (!to) return text;

            const sourceLang = detectSourceLang(text);

            // If source == target, no need to translate
            if (sourceLang === to) return text;

            // If source is not English, first go to English, then to target
            // This gives much better results for hi→ta, hi→mr etc.
            if (sourceLang !== 'en') {
                const englishFirst = await fetchTranslation(text, sourceLang, 'en');
                if (englishFirst) {
                    const finalResult = await fetchTranslation(englishFirst, 'en', to);
                    result = finalResult || englishFirst;
                } else {
                    // Fallback: try direct
                    const direct = await fetchTranslation(text, sourceLang, to);
                    result = direct || text;
                }
            } else {
                const direct = await fetchTranslation(text, 'en', to);
                result = direct || text;
            }
        }
    } catch {
        result = text;
    }

    cache.set(cacheKey, result);
    return result;
}